/**
 * Column Utilities
 * Shared utilities for column type detection and manipulation
 */

// SQL type keywords that indicate timestamp/date fields.
// Checked via `includes` which is safe for SQL types (e.g. "timestamp with time zone").
const TIMESTAMP_TYPES = ['timestamp', 'datetime', 'date', 'time'];

// Exact column names (case-insensitive) that indicate timestamp/date fields.
const TIMESTAMP_NAMES_EXACT = new Set([
  'timestamp',
  'end_timestamp',
  'start_timestamp',
  'created',
  'updated',
  'created_at',
  'updated_at',
  'date',
  'time',
  'datetime',
  'executed',
  'remediated',
]);

// Column name suffixes that strongly indicate a timestamp/date field.
const TIMESTAMP_SUFFIXES = ['_at', '_time', '_date', '_timestamp'];

/**
 * Check if a column is a timestamp/date type.
 * Uses exact name matching + suffix patterns to avoid false positives
 * (e.g. "downtime" or "candidate" are NOT timestamps).
 *
 * @param {string|Object} col - Column as string (name only) or object with name and type
 * @returns {boolean} - True if column is a timestamp type
 */
export const isTimestampColumn = (col) => {
  if (!col) return false;
  const name = typeof col === 'string' ? col : (col.name ?? '');
  const type = typeof col === 'string' ? '' : (col.type ?? '');
  const nameLower = String(name).toLowerCase();
  const typeLower = String(type).toLowerCase();

  // Extract the base column name (strip table prefix like "events.timestamp" -> "timestamp")
  const baseNameLower = nameLower.includes('.') ? nameLower.split('.').pop() : nameLower;

  const isTimestampType = TIMESTAMP_TYPES.some((t) => typeLower.includes(t));
  const isTimestampName =
    TIMESTAMP_NAMES_EXACT.has(baseNameLower) ||
    TIMESTAMP_SUFFIXES.some((suffix) => baseNameLower.endsWith(suffix));

  return isTimestampType || isTimestampName;
};

/**
 * Filter columns to get only timestamp columns
 * @param {Array} columns - Array of column objects or strings
 * @returns {Array} - Array of timestamp columns (same shape as input)
 */
export const getTimestampColumns = (columns) => {
  if (!columns || !Array.isArray(columns)) return [];
  return columns.filter(isTimestampColumn);
};

/**
 * Filter columns to get only non-timestamp (value) columns
 * @param {Array} columns - Array of column objects
 * @returns {Array} - Array of non-timestamp columns
 */
export const getValueColumns = (columns) => {
  if (!columns || !Array.isArray(columns)) return [];
  return columns.filter((col) => !isTimestampColumn(col));
};
